import { call, delay, put, race, select, take, takeLatest, takeLeading } from 'redux-saga/effects'
import { t } from 'i18next'
import * as Sentry from '@sentry/react'

import { selectAuthData } from '@/containers/Login/AuthSelectors'
import { clearAuth } from '@/containers/Login/AuthSlice'
import { buildUrl, callApi } from '@/shared/tools/apiSagaHelper'
import getAPIConfig from '@/shared/tools/getAPIConfig'
import { showSnackbar, SNACKBAR_TYPES } from '@/shared/tools/notifications/notification'

import {
  fetchSettingsFailure,
  fetchSettingsSuccess,
  fetchStateFailure,
  fetchStateSuccess,
  markWrite,
  setExporting,
  setLimitModalOpen,
  setSavingSettings,
  setSubmitting,
} from './breakManagementSlice'
import { downloadFile } from './downloadFile'
import { AgentBreaksRoutes } from './types'

export const POLL_INTERVAL_MS = 15000
const LIMIT_REACHED = 'LIMIT_REACHED'
const BREAK_NOT_FOUND = 'BREAK_NOT_FOUND'

interface ApiRequest {
  endpoint?: string
  method?: string
  payload?: unknown
  enqueue?: unknown
  showErrorSnackbar?: boolean
  asteriskReplacement?: number
}

export const requestApi = callApi as unknown as (request: ApiRequest) => Generator
type FetchConfig = Parameters<typeof fetch>[1]

export const fetchFile = (url: string, config: FetchConfig): Promise<Response> => fetch(url, config)

export const breakManagementActions = {
  START_POLLING: 'BREAK_MANAGEMENT/START_POLLING',
  STOP_POLLING: 'BREAK_MANAGEMENT/STOP_POLLING',
  START_BREAK: 'BREAK_MANAGEMENT/START_BREAK',
  STOP_BREAK: 'BREAK_MANAGEMENT/STOP_BREAK',
  FORCE_STOP_BREAK: 'BREAK_MANAGEMENT/FORCE_STOP_BREAK',
  LOAD_SETTINGS: 'BREAK_MANAGEMENT/LOAD_SETTINGS',
  UPDATE_SETTINGS: 'BREAK_MANAGEMENT/UPDATE_SETTINGS',
  EXPORT: 'BREAK_MANAGEMENT/EXPORT',
}

const getRoutes = (authData: any): AgentBreaksRoutes => authData?.extraRoutes?.agentBreaks ?? {}

export const breakErrorMessage = (error: any): string => {
  if (error?.status === 403) return t('breakManagement:forbidden')
  if (error?.responseData?.error?.code === BREAK_NOT_FOUND) return t('breakManagement:not_found')
  if (error?.status === 422) return t('breakManagement:invalid')

  return t('breakManagement:error')
}

const isLimitReached = (error: any) =>
  error?.status === 409 && error?.responseData?.error?.code === LIMIT_REACHED

export function* notifyError(enqueue: any, error: unknown): Generator {
  yield call(showSnackbar, enqueue, { type: SNACKBAR_TYPES.ERROR, message: breakErrorMessage(error) })
}

export function* fetchBreakState(): Generator {
  try {
    const routes = getRoutes(yield select(selectAuthData))
    if (!routes.state) {
      yield put(fetchStateFailure(t('breakManagement:relogin')))
      return
    }

    const requestedAt = Date.now()
    const response: any = yield call(requestApi, { endpoint: routes.state, showErrorSnackbar: false })
    yield put(fetchStateSuccess({ breakState: response.data, requestedAt, receivedAt: Date.now() }))
  } catch (error) {
    yield put(fetchStateFailure(breakErrorMessage(error)))
  }
}

export function* pollBreakState(): Generator {
  while (true) {
    yield call(fetchBreakState)
    yield delay(POLL_INTERVAL_MS)
  }
}

export function* watchPolling(): Generator {
  yield race({ poll: call(pollBreakState), stop: take(breakManagementActions.STOP_POLLING) })
}

export function* startBreak(action: any): Generator {
  const { force = false, enqueue } = action
  yield put(setSubmitting(true))
  yield put(markWrite(Date.now()))
  try {
    const routes = getRoutes(yield select(selectAuthData))
    yield call(requestApi, { endpoint: routes.start, method: 'POST', payload: { force }, showErrorSnackbar: false })
    yield put(setLimitModalOpen(false))
    yield call(fetchBreakState)
  } catch (error) {
    if (isLimitReached(error)) {
      yield put(setLimitModalOpen(true))
    } else {
      yield call(notifyError, enqueue, error)
    }
  } finally {
    yield put(setSubmitting(false))
  }
}

export function* stopWith(endpoint: string | undefined, breakId: number, enqueue: any): Generator {
  yield put(setSubmitting(true))
  yield put(markWrite(Date.now()))
  try {
    yield call(requestApi, { endpoint, method: 'POST', asteriskReplacement: breakId, showErrorSnackbar: false })
    yield call(fetchBreakState)
  } catch (error) {
    yield call(notifyError, enqueue, error)
  } finally {
    yield put(setSubmitting(false))
  }
}

export function* stopBreak(action: any): Generator {
  const routes = getRoutes(yield select(selectAuthData))
  yield call(stopWith, routes.stop, action.breakId, action.enqueue)
}

export function* forceStopBreak(action: any): Generator {
  const routes = getRoutes(yield select(selectAuthData))
  yield call(stopWith, routes.forceStop, action.breakId, action.enqueue)
}

export function* loadSettings(): Generator {
  try {
    const routes = getRoutes(yield select(selectAuthData))
    const response: any = yield call(requestApi, { endpoint: routes.settings, showErrorSnackbar: false })
    yield put(fetchSettingsSuccess(response.data))
  } catch (error) {
    yield put(fetchSettingsFailure(breakErrorMessage(error)))
  }
}

export function* updateSettings(action: any): Generator {
  const { settings, enqueue } = action
  yield put(setSavingSettings(true))
  yield put(markWrite(Date.now()))
  try {
    const routes = getRoutes(yield select(selectAuthData))
    const response: any = yield call(requestApi, {
      endpoint: routes.updateSettings,
      method: 'PUT',
      payload: settings,
      showErrorSnackbar: false,
    })
    yield put(fetchSettingsSuccess(response.data))
    yield call(showSnackbar, enqueue, { type: SNACKBAR_TYPES.SUCCESS, message: t('breakManagement:settings.saved') })
    yield call(fetchBreakState)
  } catch (error) {
    yield call(notifyError, enqueue, error)
  } finally {
    yield put(setSavingSettings(false))
  }
}

export function* exportBreaks(action: any): Generator {
  const { start, end, enqueue } = action
  yield put(setExporting(true))
  try {
    const authData: any = yield select(selectAuthData)
    const url = buildUrl(getRoutes(authData).export, authData.country, { start, end })
    const config = getAPIConfig({ method: 'GET', authTokenSession: authData.authTokenSession, body: null })
    const response: any = yield call(fetchFile, url, config as FetchConfig)
    if (response.status === 401) {
      yield put(clearAuth())
      return
    }
    if (!response.ok) throw Object.assign(new Error(`Error ${response.status}`), { status: response.status })

    const blob: any = yield call([response, response.blob])
    yield call(downloadFile, blob, `pause_${start}_${end}.xlsx`)
  } catch (error: any) {
    if (import.meta.env.PROD && !(error?.status < 500)) {
      Sentry.captureException(new Error('Break export failed'), { extra: { error } })
    }
    yield call(notifyError, enqueue, error)
  } finally {
    yield put(setExporting(false))
  }
}

export default function* breakManagementSagas() {
  yield takeLatest(breakManagementActions.START_POLLING, watchPolling)
  yield takeLeading(breakManagementActions.START_BREAK, startBreak)
  yield takeLeading(breakManagementActions.STOP_BREAK, stopBreak)
  yield takeLeading(breakManagementActions.FORCE_STOP_BREAK, forceStopBreak)
  yield takeLatest(breakManagementActions.LOAD_SETTINGS, loadSettings)
  yield takeLeading(breakManagementActions.UPDATE_SETTINGS, updateSettings)
  yield takeLeading(breakManagementActions.EXPORT, exportBreaks)
}
